import { BaseResource } from './base';
import {
  CommissionRuleDto,
  CreateCommissionRuleDto,
  UpdateCommissionRuleDto,
  CommissionRuleFiltersDto,
  CommissionHistoryDto,
  CommissionHistoryFiltersDto,
  ReorderCommissionRulesDto,
} from '../models/commissions';
import { RequestOptions, PaginatedResponseDto } from '../types';

export class CommissionRulesResource extends BaseResource {
  private basePath = 'commissions/rules';

  async createRule(
    data: CreateCommissionRuleDto,
    options?: RequestOptions,
  ): Promise<CommissionRuleDto> {
    return this.client.post<CommissionRuleDto>(this.basePath, data, options);
  }

  async getRule(id: string, options?: RequestOptions): Promise<CommissionRuleDto> {
    return this.client.get<CommissionRuleDto>(`${this.basePath}/${id}`, undefined, options);
  }

  async listRules(
    params?: CommissionRuleFiltersDto,
    options?: RequestOptions,
  ): Promise<PaginatedResponseDto<CommissionRuleDto>> {
    return this.paginate<CommissionRuleDto>(this.basePath, params, options);
  }

  async updateRule(
    id: string,
    data: UpdateCommissionRuleDto,
    options?: RequestOptions,
  ): Promise<CommissionRuleDto> {
    return this.client.put<CommissionRuleDto>(`${this.basePath}/${id}`, data, options);
  }

  async deleteRule(id: string, options?: RequestOptions): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(`${this.basePath}/${id}`, options);
  }

  async activateRule(id: string, options?: RequestOptions): Promise<CommissionRuleDto> {
    return this.client.put<CommissionRuleDto>(`${this.basePath}/${id}/activate`, {}, options);
  }

  async deactivateRule(id: string, options?: RequestOptions): Promise<CommissionRuleDto> {
    return this.client.put<CommissionRuleDto>(`${this.basePath}/${id}/deactivate`, {}, options);
  }

  async getRuleHistory(
    id: string,
    params?: CommissionHistoryFiltersDto,
    options?: RequestOptions,
  ): Promise<PaginatedResponseDto<CommissionHistoryDto>> {
    return this.paginate<CommissionHistoryDto>(`${this.basePath}/${id}/history`, params, options);
  }

  async validateRule(
    data: CreateCommissionRuleDto,
    options?: RequestOptions,
  ): Promise<{ valid: boolean; errors?: string[]; conflicts?: CommissionRuleDto[] }> {
    return this.client.post<{ valid: boolean; errors?: string[]; conflicts?: CommissionRuleDto[] }>(
      `${this.basePath}/validate`,
      data,
      options,
    );
  }

  async getMyRules(options?: RequestOptions): Promise<CommissionRuleDto[]> {
    return this.client.get<CommissionRuleDto[]>(`${this.basePath}/me`, undefined, options);
  }

  async getApplicableRule(
    params: {
      collaboratorId: string;
      planId?: string;
      amount?: number;
      promotionId?: string;
    },
    options?: RequestOptions,
  ): Promise<CommissionRuleDto | null> {
    return this.client.get<CommissionRuleDto | null>(
      `${this.basePath}/applicable`,
      params,
      options,
    );
  }

  async reorderRules(
    data: ReorderCommissionRulesDto,
    options?: RequestOptions,
  ): Promise<CommissionRuleDto[]> {
    return this.client.patch<CommissionRuleDto[]>(`${this.basePath}/reorder`, data, options);
  }
}
